<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import { instanceKey } from '../model/catalog'
import type { NodeFit } from '../model/fit'
import {
  type Interval,
  INTERVALS,
  isInterval,
  isSortOrder,
  SORT_ORDERS,
  type SortOrder,
} from '../model/ranking'
import type { SlotId } from './types'

const props = defineProps<{
  fits: readonly NodeFit[]
  sort: SortOrder
  interval: Interval
  hours: number
  primary: string | null
  secondary: string | null
  /** Whether the B slot is on screen; only then are rows tagged A and B. */
  comparing: boolean
  activeSlot: SlotId
}>()

const emit = defineEmits<{
  select: [string]
  'update:sort': [SortOrder]
  'update:interval': [Interval]
}>()

const { t } = useI18n()
const format = useFormat()

function slotsOf(fit: NodeFit): SlotId[] {
  const key = instanceKey(fit.instance)
  const slots: SlotId[] = []
  if (key === props.primary) slots.push('a')
  if (props.comparing && key === props.secondary) slots.push('b')
  return slots
}

function onSort(event: Event): void {
  const target = event.target
  if (target instanceof HTMLSelectElement && isSortOrder(target.value))
    emit('update:sort', target.value)
}

function onInterval(event: Event): void {
  const target = event.target
  if (target instanceof HTMLSelectElement && isInterval(target.value))
    emit('update:interval', target.value)
}
</script>

<template>
  <div class="list">
    <div class="list__controls">
      <div class="list__field">
        <label for="sort">{{ t('list.sort') }}</label>
        <select id="sort" :value="sort" @change="onSort">
          <option v-for="order in SORT_ORDERS" :key="order" :value="order">
            {{ t(`list.sortBy.${order}`) }}
          </option>
        </select>
      </div>
      <div class="list__field">
        <label for="interval">{{ t('list.interval') }}</label>
        <select id="interval" :value="interval" @change="onInterval">
          <option v-for="option in INTERVALS" :key="option" :value="option">
            {{ t(`interval.${option}`) }}
          </option>
        </select>
      </div>
    </div>

    <p class="list__count" aria-live="polite">
      {{ t('list.count', { count: format.count(fits.length) }, fits.length) }}
      <template v-if="comparing">
        · {{ t('list.filling', { slot: activeSlot.toUpperCase() }) }}</template
      >
    </p>

    <div class="list__scroll" tabindex="-1">
      <table v-if="fits.length" class="list__table">
        <caption class="visually-hidden">
          {{
            t('list.caption')
          }}
        </caption>
        <thead>
          <tr>
            <th scope="col">{{ t('list.columns.name') }}</th>
            <th scope="col" class="list__num">{{ t('list.columns.vcpu') }}</th>
            <th scope="col" class="list__num">{{ t('list.columns.memory') }}</th>
            <th scope="col" class="list__num">{{ t('list.columns.pods') }}</th>
            <th scope="col" class="list__num">{{ t(`list.columns.costPerPod.${interval}`) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="fit in fits"
            :key="fit.instance.name"
            :class="{
              'list__row--selected': slotsOf(fit).length > 0,
              'list__row--none': fit.pods === 0,
            }"
          >
            <th scope="row">
              <button
                type="button"
                class="list__pick mono"
                :aria-pressed="slotsOf(fit).length > 0"
                @click="emit('select', instanceKey(fit.instance))"
              >
                {{ fit.instance.name }}
              </button>
              <span v-for="slot in slotsOf(fit)" :key="slot" class="list__tag">
                <span class="visually-hidden">{{
                  t('slot.label', { slot: slot.toUpperCase() })
                }}</span>
                <span aria-hidden="true">{{ slot.toUpperCase() }}</span>
              </span>
            </th>
            <td class="list__num mono">{{ format.count(fit.instance.vcpu) }}</td>
            <td class="list__num mono">{{ format.memory(fit.memory.capacity) }}</td>
            <td class="list__num mono">{{ format.count(fit.pods) }}</td>
            <td class="list__num mono">{{ format.price(fit.costPerPod * hours) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="list__empty">{{ t('list.empty') }}</p>
    </div>
  </div>
</template>

<style scoped>
.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.list__controls {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.list__field {
  display: flex;
  flex: 1 1 9rem;
  flex-direction: column;
  gap: var(--space-1);
}

label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

select {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.list__count {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.list__scroll {
  max-height: 34rem;
  overflow: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.list__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

.list__table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: var(--space-2);
  background: var(--color-surface-raised);
  box-shadow: inset 0 -1px 0 var(--color-border);
  color: var(--color-text-muted);
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
}

.list__table td,
.list__table tbody th {
  padding: 0 var(--space-2);
  border-top: 1px solid var(--color-border);
  font-weight: 400;
  text-align: left;
  white-space: nowrap;
}

.list__table .list__num {
  text-align: right;
}

.list__pick {
  padding: var(--space-2) 0;
  border: 0;
  background: none;
  color: var(--color-accent);
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
}

.list__pick:hover {
  text-decoration: underline;
}

.list__row--selected {
  background: var(--color-accent-subtle);
}

.list__row--none td {
  color: var(--color-text-muted);
}

.list__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  margin-left: var(--space-1);
  border-radius: var(--radius-sm);
  background: var(--color-accent);
  color: var(--color-text-inverted);
  font-size: 0.7rem;
  font-weight: 700;
}

.list__empty {
  margin: 0;
  padding: var(--space-6);
  color: var(--color-text-muted);
  text-align: center;
}
</style>
